import { useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useActiveTabId } from '@/hooks/useActiveTabId'
import { useAppTabs } from '@/hooks/useAppTabs'
import { useMountEffect } from '@/hooks/useMountEffect'
import { useAppStore } from '@/stores/app'
import { useCloseOwners, usePendingClose } from '@/stores/closeOwners'
import { useTabDrafts } from '@/stores/tabDrafts'

/**
 * The unsaved-work gate for closing a tab. Mounted ONCE by ContentPanelArea,
 * outside the per-tab `<Activity>` trees — a dialog rendered inside the tab it
 * is about would be frozen the moment that tab stopped being active.
 *
 * `useAppTabs.requestClose` parks the request in `usePendingClose` and (when
 * needed) navigates to the tab. This renders only once that tab is actually
 * active, so the dialog never describes a tab the user cannot see.
 */
export function CloseTabDialog() {
  const pending = usePendingClose((s) => s.pending)
  const activeId = useActiveTabId()
  if (!pending || pending.tabId !== activeId) return null
  // Keyed so a second close request starts from clean local state.
  return <CloseTabDialogBody key={pending.tabId} />
}

function CloseTabDialogBody() {
  const pending = usePendingClose((s) => s.pending)
  const settle = usePendingClose((s) => s.settle)
  const { performClose } = useAppTabs()
  const tabId = pending?.tabId
  // Read live, not parked: a rename while the prompt is up must not leave the
  // dialog naming the old title.
  const title = useAppStore(
    (s) => s.tabs.items.find((t) => t.id === tabId)?.title
  )
  const owner = useCloseOwners((s) => (tabId ? s.owners.get(tabId) : undefined))
  const [ready, setReady] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // One tick of delay before the first paint. Activating the tab mounts its
  // editor in the same commit, and the editor registers its close owner in a
  // mount effect — painting now would show a Save-less dialog for a tab that
  // can save, then grow a Save button under the user's cursor.
  useMountEffect(() => setReady(true))

  const handleSave = async () => {
    if (!owner || !tabId) return
    setIsSaving(true)
    setError(null)
    const result = await owner.save()
    setIsSaving(false)
    // A failed save leaves the dialog up and the request pending: the draft is
    // still the only copy of the user's work, so closing now would lose it.
    if (!result.ok) {
      setError(result.message)
      return
    }
    performClose(tabId)
  }

  const handleDiscard = () => {
    if (!tabId) return
    useTabDrafts.getState().clearDraft(tabId)
    performClose(tabId)
  }

  if (!pending || !ready) return null

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        // Escape and overlay dismissal both route here — treat them as Cancel.
        if (!open && !isSaving) settle('cancelled')
      }}>
      {/* Default width, not `sm`: the sm footer is a 2-column grid and this
          dialog has three actions. */}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Save changes before closing?</AlertDialogTitle>
          <AlertDialogDescription>
            “{title}” has unsaved changes. Discarding them can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {/* A composite tab saves many editors at once, so this can be a joined
            list ("Failed to save: Robocall message — 502; Voicemail — network
            error"). Cap the height and let it scroll rather than pushing the
            footer buttons off-screen. */}
        {error && (
          <p
            role="alert"
            className="max-h-24 overflow-y-auto text-sm break-words text-destructive">
            {error}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSaving}>
            Keep editing
          </AlertDialogCancel>
          {/* A plain Button, not AlertDialogAction: Radix's Action closes the
              dialog itself, which would fire onOpenChange and settle the
              request as 'cancelled' behind performClose's back. */}
          <Button
            variant="destructive"
            disabled={isSaving}
            onClick={handleDiscard}>
            Discard
          </Button>
          {/* Only tabs whose editor registered a close owner can be saved from
              here. Without one there is nothing to call, so the action is
              omitted rather than rendered dead. */}
          {owner && (
            <AlertDialogAction
              disabled={isSaving}
              onClick={(e) => {
                // Radix closes the dialog synchronously on Action click;
                // prevent that so it stays up (disabled) while the save runs
                // and only closes once the save succeeds.
                e.preventDefault()
                void handleSave()
              }}>
              {isSaving ? 'Saving…' : 'Save'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
